/**
 * Canonical web-environment resolver for the sites/ surfaces (www,
 * course-player). ADR 0012 vocabulary — `production | staging |
 * development | unknown`, never silently default — same values the
 * dashboard's `frontend/src/utils/appEnvironment.ts` (ISS-260806-n89b) and
 * mobile's `app/utils/appEnvironment.ts` (ADR 0015) return, so a
 * cross-surface view can group all four by one value.
 *
 * Lives under `analytics/` (not a general `env/` module) because that is
 * this pass's write scope for `shared/src/**` — its purpose is broader than
 * PostHog (it also feeds Sentry's `environment` tag on both surfaces), but
 * there is exactly one derivation and every consumer must route through it.
 *
 * PREFERS AN EXPLICIT VALUE. On the real deploy path
 * (`.github/actions/deploy-static-sites`), one genuinely is available: the
 * action fetches `neumind/{env}/frontend/env` — the SAME derived secret the
 * dashboard's runtime config.js reads, which carries `VITE_ENVIRONMENT` via
 * terraform's `frontend_environment_env` local
 * (`infra/terraform/modules/secrets/main.tf`) — and exports every key to the
 * job env BEFORE the site's `vite build` step. Vite's `loadEnv()` inlines
 * that shell export into `import.meta.env.VITE_ENVIRONMENT` exactly like it
 * already does for `VITE_DASHBOARD_ORIGIN` (see
 * `sites/www/src/lib/constants.ts`), so this is not a hypothetical channel —
 * it is the same mechanism already relied on elsewhere in this build.
 *
 * FALLS BACK TO MODE, BUT ONLY WHERE MODE CANNOT LIE. Vite's `vite build`
 * silently defaults `MODE` to `"production"` when no `--mode` flag is
 * passed — that default is indistinguishable, from inside the bundle, from
 * a genuine production build. `"development"` and `"staging"` are never
 * silent defaults (the former only from `vite dev` or an explicit
 * `--mode development`; the latter only from an explicit `--mode staging`),
 * so those two are trusted from MODE. A MODE of `"production"` (or anything
 * unrecognised) with no explicit value reports `"unknown"` + a console
 * warning — the honest outcome for "an unaudited build path", rather than
 * resurrecting the exact silent-production bug this resolver exists to fix.
 */
const KNOWN = [
    "production",
    "staging",
    "development",
];
const MODE_TRUSTED_WITHOUT_EXPLICIT = [
    "development",
    "staging",
];
function normalize(value) {
    const trimmed = value?.trim().toLowerCase();
    return trimmed ? trimmed : undefined;
}
/**
 * Resolve the deployed/running environment for a sites/ (Vite) surface.
 *
 * @param explicit  `import.meta.env.VITE_ENVIRONMENT`, if the build set it.
 * @param mode      `import.meta.env.MODE`.
 */
export function resolveWebEnvironment(explicit, mode) {
    const normalizedExplicit = normalize(explicit);
    if (normalizedExplicit) {
        if (KNOWN.includes(normalizedExplicit)) {
            return normalizedExplicit;
        }
        console.warn(`[environment] Unrecognised VITE_ENVIRONMENT "${explicit}" — falling back.`);
    }
    const normalizedMode = normalize(mode);
    if (normalizedMode &&
        MODE_TRUSTED_WITHOUT_EXPLICIT.includes(normalizedMode)) {
        return normalizedMode;
    }
    console.warn(`[environment] Could not determine environment (VITE_ENVIRONMENT=${explicit ?? "unset"}, MODE=${mode ?? "unset"}) — reporting "unknown" rather than guessing.`);
    return "unknown";
}
